import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Clock, Palette, Wallet, Mail, Phone, PartyPopper, Check, Send } from "lucide-react";
import api from "@/lib/api";

function BookingForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", event_date: "", location: "", headcount: "", message: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) return toast.error("Name and email please!");
    setBusy(true);
    try { await api.post("/event-bookings", f); setDone(true); }
    catch { toast.error("Something went wrong — try again"); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="brut border-ink bg-green-100 p-6 text-center" data-testid="booking-success">
      <Check className="w-8 h-8 mx-auto mb-2" />
      <p className="font-display font-bold text-xl">Request sent! 🎉</p>
      <p className="text-ink/70">I'll be in touch soon about your event.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto" data-testid="booking-form">
      <input className="brut-input" placeholder="Your name*" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name" />
      <input className="brut-input" type="email" placeholder="Email*" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" />
      <input className="brut-input" placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone" />
      <input className="brut-input" placeholder="Event date" value={f.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="booking-date" />
      <input className="brut-input" placeholder="Location / venue" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="booking-location" />
      <input className="brut-input" placeholder="Approx. headcount" value={f.headcount} onChange={(e) => set("headcount", e.target.value)} data-testid="booking-headcount" />
      <textarea className="brut-input sm:col-span-2 min-h-[90px]" placeholder="Tell me about your event…" value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="booking-message" />
      <button type="submit" disabled={busy} className="brut-btn bg-bubble text-white sm:col-span-2 justify-center text-lg" data-testid="booking-submit">
        <Send className="w-5 h-5" /> {busy ? "Sending…" : "Send booking request"}
      </button>
    </form>
  );
}

const DETAILS = [
  { icon: Clock, title: "$180 / hour", text: "2-hour minimum booking." },
  { icon: Palette, title: "Black & white, heads only", text: "Fast, lively line caricatures — perfect for live events." },
  { icon: Wallet, title: "10% down to book", text: "A small booking fee secures your date; balance due at the event." },
];

export default function EventsPage() {
  return (
    <div className="max-w-4xl mx-auto px-5 py-14" data-testid="events-page">
      <Helmet>
        <title>Live Event Caricatures in Grand Forks & Fargo | Art by Jason Caricatures</title>
        <meta name="description" content="Book Jason for live caricature entertainment at your event — weddings, parties, corporate & special occasions. $180/hr, 2-hour minimum, black & white heads. Serving Grand Forks & Fargo, ND." />
      </Helmet>

      <div className="text-center mb-12">
        <span className="tag-pill bg-sun mb-4"><PartyPopper className="w-4 h-4" /> Live event entertainment</span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl">Events &amp; live caricatures</h1>
        <p className="text-ink/70 text-lg mt-4 max-w-2xl mx-auto">
          Want the life of the party? I draw fast, funny black-and-white caricatures live at weddings, parties, and special occasions — your guests leave with a keepsake they'll never forget.
        </p>
      </div>

      {/* Pricing card */}
      <div className="brut-card p-8 mb-8" data-testid="events-pricing">
        <div className="text-center mb-6">
          <div className="font-display font-bold text-5xl">$180<span className="text-2xl text-ink/60">/hr</span></div>
          <p className="text-ink/60 mt-1">2-hour minimum</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {DETAILS.map((d) => (
            <div key={d.title} className="brut border-ink bg-white p-4 text-center">
              <span className="inline-grid place-items-center w-11 h-11 rounded-xl border-2 border-ink bg-sun mb-2"><d.icon className="w-5 h-5" /></span>
              <div className="font-display font-bold">{d.title}</div>
              <p className="text-sm text-ink/60 mt-1">{d.text}</p>
            </div>
          ))}
        </div>
        <ul className="mt-6 space-y-2 text-sm max-w-md mx-auto">
          {["$180 per hour, 2-hour minimum", "Black & white, heads only", "10% down booking fee to reserve your date"].map((t) => (
            <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-bubble shrink-0" /> {t}</li>
          ))}
        </ul>
      </div>

      {/* Contact / booking */}
      <div className="brut-card p-8 bg-grape/15" data-testid="events-contact">
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-center">Request a booking</h2>
        <p className="text-ink/70 mt-2 mb-6 text-center">Send your details and I'll get right back to you — or reach out directly below.</p>
        <BookingForm />
        <div className="flex flex-wrap gap-3 justify-center mt-6">
          <a href="mailto:artbyjason22@gmail.com?subject=Event%20Caricature%20Booking" className="brut-btn bg-white text-ink" data-testid="events-email">
            <Mail className="w-5 h-5" /> artbyjason22@gmail.com
          </a>
          <a href="tel:+12182304461" className="brut-btn bg-sun" data-testid="events-phone">
            <Phone className="w-5 h-5" /> 218-230-4461
          </a>
        </div>
      </div>

      <div className="text-center mt-8">
        <Link to="/gallery" className="brut-btn bg-white">See example work</Link>
      </div>
    </div>
  );
}
